import * as React from 'react';

import { X } from 'lucide-react';
import type { ModalOverlayProps, HeadingProps } from 'react-aria-components';
import {
  Modal as AriaModal,
  Dialog as AriaDialog,
  Heading as AriaHeading,
  ModalOverlay as AriaModalOverlay,
  OverlayTriggerStateContext,
} from 'react-aria-components';

import { cn } from '@avodah-inc/ui/utils';

import { Button } from '../../atoms/button';

export const ModalHeader = ({
  className,
  close,
  closeButtonColor,
  ...props
}: HeadingProps & { close: () => void; closeButtonColor?: string }) => {
  return (
    <AriaHeading
      className={cn(
        'flex items-center justify-between border-b px-6 py-4 pb-4 text-left text-lg font-bold text-gray-900',
        className,
      )}
      {...props}
    >
      {props.children}
      <Button variant="text" onPress={close} className="!px-0">
        <X
          className={cn('h-6 w-6 font-light', closeButtonColor)}
          strokeWidth="1.5px"
        />
      </Button>
      <span className="sr-only">Close</span>
    </AriaHeading>
  );
};

type ModalFooterProps = {
  className?: string;
  onConfirmButtonProps?: React.ComponentPropsWithoutRef<typeof Button>;
  onCancelButtonProps?: React.ComponentPropsWithoutRef<typeof Button>;
  isDisabled?: boolean;
  cancelDataTestId?: string;
  confirmDataTestId?: string;
};

export const ModalFooter = ({
  className,
  onCancelButtonProps,
  onConfirmButtonProps,
  isDisabled,
  cancelDataTestId,
  confirmDataTestId,
}: ModalFooterProps) => {
  const state = React.useContext(OverlayTriggerStateContext);

  return (
    <div
      className={cn(
        'flex items-center justify-between border-t px-6 py-5',
        className,
      )}
    >
      <Button
        variant="text"
        children="Cancel"
        onPress={() => {
          if (state) state.close();
        }}
        data-testid={cancelDataTestId}
        {...onCancelButtonProps}
      />
      <Button
        isDisabled={isDisabled}
        children="confirm"
        data-testid={confirmDataTestId}
        {...onConfirmButtonProps}
      />
    </div>
  );
};

export type ModalProps = Omit<ModalOverlayProps, 'children'> & {
  children?: (({ close }: { close: () => void }) => JSX.Element) | JSX.Element;
  title?: string;
  onClose?: () => void;
};

export const Modal = ({
  title = 'Modal',
  children,
  className,
  onClose,
  closeButtonColor = 'text-primary',
  ...props
}: ModalProps & { closeButtonColor?: string }) => {
  return (
    <AriaModalOverlay
      {...props}
      className={({ isEntering, isExiting }) => `
          fixed inset-0 z-10 flex min-h-full items-center justify-center overflow-y-auto bg-black/30 p-4 backdrop-blur
          ${isEntering ? 'animate-in fade-in duration-300 ease-out' : ''}
          ${isExiting ? 'animate-out fade-out duration-200 ease-in' : ''}
        `}
    >
      <AriaModal className={cn('w-full max-w-lg rounded bg-white', className)}>
        <AriaDialog className="h-full w-full outline-none">
          {({ close }) => {
            const handleClose = () => {
              onClose?.();
              close();
            };
            const body =
              typeof children === 'function'
                ? children({ close: handleClose })
                : children;
            return (
              <>
                <ModalHeader
                  close={handleClose}
                  closeButtonColor={closeButtonColor}
                >
                  {title}
                </ModalHeader>
                <>{body}</>
              </>
            );
          }}
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
};
